/**
 * Choose a keybook key and one of its fields — the keys this person can open, the field names read from the key (never
 * its values). Shared by the app's Variables and secrets and the install setup's key step: every kind of key has its own
 * fields, so they are always read from the key itself.
 *
 * Display (owner-approved redesign): each key with its connector icon (the Keybook page's) and translated type; a
 * picked key folds the list into one card with Change; a key with one field uses it (picked for you), with several the
 * field is a row of chips. The key list follows the Keybook live (renamed / deleted / new keys), refreshed in place.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, Loader2, Search } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { getActiveTenantId } from '@papan/api-client'
import { useRealtimeOptional } from '@papan/realtime/client'
import { keybookApi } from '../client/api.js'
import { connectorIcon } from './key-icons.js'
import { Choice } from './Choice.js'

export interface KeyFieldChoice { entryId: string | null; field: string }

type KeyOption = { id: string; label: string; credentialType: string }

const toOptions = (entries: Array<{ id: string; customLabel?: string | null; sourceLabel: string; credentialType: string; permission?: unknown }>): KeyOption[] =>
  entries
    .filter(e => !!e.permission)
    .map(e => ({ id: e.id, label: e.customLabel || e.sourceLabel, credentialType: e.credentialType }))
    .sort((a, b) => a.label.localeCompare(b.label))

export function KeyFieldPicker({ value, onChange, idPrefix = 'pdapp-env', fallback, onPickedKey }: {
  value: KeyFieldChoice
  onChange: (next: KeyFieldChoice) => void
  /** Prefix for element ids / test ids (several pickers can sit on one screen). */
  idPrefix?: string
  /** What to call the picked key when it isn't in this person's list (yet, or at all) — e.g. the label the server
   *  sent for the variable being edited. */
  fallback?: { label: string; credentialType?: string | null }
  /** Display only: told how the picked key is shown (its name and type), or null when none is shown — so the host can
   *  show the same chip once the choice is saved. */
  onPickedKey?: (key: { label: string; credentialType: string } | null) => void
}) {
  const { t } = useTranslation('keybook')
  const [keys, setKeys] = useState<KeyOption[] | null>(null)
  const [query, setQuery] = useState('')
  const [fields, setFields] = useState<string[] | null>(null)
  // Display only: the picked key's list reopened to change it.
  const [changing, setChanging] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  // Every read of the key list — the first one and each live refresh — takes a number from ONE counter, and an answer
  // is applied only if it is newer than the last one applied: the newest successful read wins, whatever order they
  // land in (a slow first read never puts an old name back over a refresh).
  const readSeq = useRef(0)
  const appliedSeq = useRef(0)

  // The keys this person can read in the active org.
  useEffect(() => {
    const tenantId = getActiveTenantId()
    if (!tenantId) { setKeys([]); return }
    let live = true
    const mine = ++readSeq.current
    keybookApi.listEntries(tenantId)
      .then(r => { if (live && mine > appliedSeq.current) { appliedSeq.current = mine; setKeys(toOptions(r.entries)) } })
      .catch(() => { if (live && mine > appliedSeq.current) { appliedSeq.current = mine; setKeys([]) } })
    return () => { live = false }
  }, [])

  // A key renamed, deleted or added in the Keybook shows here at once: re-read the list IN PLACE on the Keybook's event
  // (debounced). A failed refresh keeps the list.
  const onKeybookEvent = useRealtimeOptional()?.onKeybookEvent
  useEffect(() => {
    if (!onKeybookEvent) return
    let timer: ReturnType<typeof setTimeout> | null = null
    let live = true
    const off = onKeybookEvent(event => {
      const tenantId = getActiveTenantId()
      if (!tenantId || event.tenantId !== tenantId) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        const mine = ++readSeq.current
        keybookApi.listEntries(tenantId)
          .then(r => { if (live && mine > appliedSeq.current) { appliedSeq.current = mine; setKeys(toOptions(r.entries)) } })
          .catch(() => {})
      }, 300)
    })
    return () => { live = false; off(); if (timer) clearTimeout(timer) }
  }, [onKeybookEvent])

  // The picked key's field names; a field the key doesn't have falls back to its first.
  useEffect(() => {
    setFields(null)
    if (!value.entryId) return
    let live = true
    const entryId = value.entryId
    keybookApi.getEntryFieldNames(entryId)
      .then(r => {
        if (!live) return
        setFields(r.fields)
        if (!r.fields.includes(value.field)) onChange({ entryId, field: r.fields[0] ?? '' })
      })
      .catch(() => { if (live) setFields([]) })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-read only when the KEY changes
  }, [value.entryId])

  // Picking a key folds its list into a card, and Change does the reverse: either way the button pressed goes away,
  // so focus moves to what replaced it (Change on the card, or the list's search box).
  const focusNext = useRef<'change' | 'search' | null>(null)
  useEffect(() => {
    const want = focusNext.current
    if (!want) return
    const el = rootRef.current?.querySelector<HTMLElement>(`[data-testid="${idPrefix}-${want === 'change' ? 'change-key' : 'key-filter'}"]`)
    if (el) { focusNext.current = null; el.focus() }
  })

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return (keys ?? []).filter(k => !q || k.label.toLowerCase().includes(q))
  }, [keys, query])

  // A key's type is free text: only a string translation is shown (a type named after an object key such as
  // `constructor` would otherwise come back as a function, not a label).
  const typeName = (credentialType: string | null | undefined) => {
    if (!credentialType) return ''
    const label: unknown = t(`credentialType.${credentialType}`, { defaultValue: credentialType })
    return typeof label === 'string' ? label : credentialType
  }

  const picked = value.entryId
    ? keys?.find(k => k.id === value.entryId) ?? (fallback ? { id: value.entryId, label: fallback.label, credentialType: fallback.credentialType ?? '' } : null)
    : null
  const showList = !value.entryId || changing

  // Display only: the host learns how the picked key is shown (see `onPickedKey`).
  const pickedLabel = picked?.label ?? null
  const pickedType = picked?.credentialType ?? ''
  const onPickedKeyRef = useRef(onPickedKey)
  onPickedKeyRef.current = onPickedKey
  useEffect(() => {
    onPickedKeyRef.current?.(pickedLabel === null ? null : { label: pickedLabel, credentialType: pickedType })
  }, [pickedLabel, pickedType])

  return (
    <div ref={rootRef} className="rounded-md border themed-border overflow-hidden">
      {!showList ? (
        // Picked: one card for the key, with Change to pick another.
        <div className="flex items-center gap-2.5 px-3 py-2.5" data-testid={`${idPrefix}-picked-key`}>
          {(() => { const Icon = connectorIcon({ credentialType: picked?.credentialType ?? '' }); return <span className="shrink-0 p-1.5 rounded-md themed-bg-tertiary themed-icon"><Icon size={14} /></span> })()}
          <span className="min-w-0 flex-1">
            <span className="block text-sm themed-text truncate">{picked?.label ?? t('appEnv.aKey')}</span>
            {picked?.credentialType && <span className="block text-[11px] themed-text-muted">{typeName(picked.credentialType)}</span>}
          </span>
          <button type="button" onClick={() => { focusNext.current = 'search'; setChanging(true) }} className="text-xs px-2.5 py-1 rounded themed-btn-secondary shrink-0" data-testid={`${idPrefix}-change-key`}>
            {t('appEnv.changeKey')}
          </button>
        </div>
      ) : (
        <>
          <div className="relative border-b themed-border">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 themed-text-muted" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t('appEnv.searchKeys')}
              aria-label={t('appEnv.searchKeys')}
              className="text-sm pl-8 pr-2 py-1.5 w-full bg-transparent themed-text outline-none"
              data-testid={`${idPrefix}-key-filter`}
            />
          </div>
          {keys === null ? (
            <p className="text-xs themed-text-muted px-3 py-2"><Loader2 size={12} className="inline animate-spin mr-1" />{t('appEnv.loadingKeys')}</p>
          ) : visible.length === 0 ? (
            <p className="text-xs themed-text-muted px-3 py-2">{keys.length === 0 ? t('appEnv.noKeys') : t('appEnv.noKeyMatch')}</p>
          ) : (
            <ul className="max-h-52 overflow-y-auto p-1 space-y-0.5" role="listbox" aria-label={t('appEnv.sourceKeybook')}>
              {visible.map(k => {
                const Icon = connectorIcon(k)
                const on = value.entryId === k.id
                return (
                  <li key={k.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={on}
                      onClick={() => { focusNext.current = 'change'; setChanging(false); onChange({ entryId: k.id, field: on ? value.field : '' }) }}
                      className={`w-full text-left px-2 py-1.5 rounded flex items-center gap-2.5 ${on ? 'bg-blue-500/10 themed-text' : 'themed-text themed-bg-hover'}`}
                      data-testid={`${idPrefix}-key-option`}
                    >
                      <span className="shrink-0 p-1 rounded themed-bg-tertiary themed-icon"><Icon size={12} /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs truncate">{k.label}</span>
                        <span className="block text-[10px] themed-text-muted">{typeName(k.credentialType)}</span>
                      </span>
                      {on && <Check size={13} className="shrink-0 text-blue-400" />}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </>
      )}
      {/* A key with one field uses it (picked for you); with several, choose one. */}
      {value.entryId && !showList && (
        fields === null ? (
          <div className="px-3 py-2 border-t themed-border"><Loader2 size={12} className="animate-spin themed-text-muted" /></div>
        ) : fields.length === 0 ? (
          <p className="px-3 py-2 border-t themed-border text-xs text-red-400">{t('appEnv.noFields')}</p>
        ) : fields.length > 1 ? (
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t themed-border">
            <span className="text-xs themed-text-muted">{t('appEnv.field')}</span>
            <Choice
              label={t('appEnv.field')}
              value={value.field}
              options={fields.map(f => ({ key: f, label: <span className="font-mono">{f}</span>, testId: `${idPrefix}-field-${f}` }))}
              onChange={f => onChange({ entryId: value.entryId, field: f })}
              testId={`${idPrefix}-field`}
            />
          </div>
        ) : null
      )}
    </div>
  )
}
